import React, { useState } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Space, 
  Row, 
  Col, 
  Divider,
  Checkbox,
  Alert,
  Modal
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  EyeInvisibleOutlined, 
  EyeTwoTone,
  BookOutlined 
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import authService from '../services/authService';
import { API_ENDPOINTS } from '../config/api-endpoints';
import { FacebookSignInButton } from '../components/auth/FacebookLoginButton';

const { Title, Text } = Typography;

interface LoginFormData {
  email: string;
  password: string;
  remember?: boolean;
}

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [forgotPasswordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{type: 'success' | 'error' | 'info', message: string} | null>(null);
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const handleLogin = async (values: LoginFormData) => {
    setLoading(true);
    try {
      await authService.login(values.email, values.password);
      
      setAlertMessage({type: 'success', message: 'Сайн байна уу. Тавтай морил!'});
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error: any) {
      console.error('Login error:', error);
      // Accounts migrated to Facebook must sign in with Facebook instead of a password
      if (error.message && error.message.includes('Facebook login required')) {
        setAlertMessage({type: 'info', message: 'Энэ бүртгэл Facebook-тэй холбогдсон байна. Доорх Facebook-ээр нэвтрэх товчийг ашиглана уу.'});
      } else {
        setAlertMessage({type: 'error', message: error.message || 'Email эсвэл Нууц үг буруу байна'});
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookLoginSuccess = () => {
    setAlertMessage({type: 'success', message: 'Сайн байна уу. Тавтай морил!'});
    setTimeout(() => {
      navigate('/dashboard');
    }, 1500);
  };

  const handleFacebookLoginError = (message: string) => {
    if (message.includes('Facebook account is not linked')) {
      setAlertMessage({type: 'info', message: 'Энэ Facebook акаунт бүртгэлтэй холбогдоогүй байна. Эхлээд Email, Нууц үгээрээ нэвтэрч Facebook-ээ холбоно уу.'});
    } else {
      setAlertMessage({type: 'error', message});
    }
  };

  const handleTestWrongCredentials = () => {
    setAlertMessage({type: 'error', message: 'Email эсвэл Нууц үг буруу байна. Дахин оролдоно уу.'});
  };

  const handleSocialLogin = (provider: string) => {
    setAlertMessage({type: 'info', message: `${provider} нэвтрэх боломж удахгүй нэмэгдэнэ!`});
  };

  const handleForgotPassword = async (values: { email: string }) => {
    setForgotPasswordLoading(true);
    try {
      const baseURL = process.env.REACT_APP_API_URL ;
      const response = await fetch(`${baseURL}${API_ENDPOINTS.FORGOT_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: values.email }),
      });

      const result = await response.json();

      if (response.ok && result.status === 'success') {
        setAlertMessage({
          type: 'success', 
          message: 'Нууц үг шинэчлэх заавар таны имэйл хаяг руу илгээгдлээ'
        });
        setShowForgotPassword(false);
        forgotPasswordForm.resetFields();
      } else {
        throw new Error(result.message || 'Алдаа гарлаа');
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      setAlertMessage({
        type: 'error', 
        message: error.message || 'Имэйл илгээхэд алдаа гарлаа. Дахин оролдоно уу.'
      });
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'white',
      backgroundImage: 'url(  /images/lms-login.png)',
      backgroundSize: 'contain',
      backgroundPosition: 'center bottom',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '8px' : '24px'
    }}>
      <Row style={{ width: '100%', maxWidth: '1200px' }}>
        <Col xs={24} lg={12} style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: isMobile ? '0' : '0 24px'
        }}>
          {!isMobile && (
            <div style={{ textAlign: 'center', color: 'white' }}>
              {/* <img 
                src="/images/logo.png" 
                alt="ICER Logo" 
                className="logo-image"
                style={{ 
                  width: '400px', 
                  height: '120px',
                  marginBottom: '24px',
                  objectFit: 'contain',
                  borderRadius: '12px'
                }}
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'block';
                  }
                }}
              /> */}
              {/* <BookOutlined 
                className="logo-fallback" 
                style={{ 
                  fontSize: '120px', 
                  marginBottom: '24px',
                  display: 'none',
                  color: 'white'
                }} 
              /> */}
              <div style={{ marginTop: '32px' }}>
                {/* <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px' }}>
                  ✨ Interactive Learning<br/>
                  📚 Expert-led Courses<br/>
                  🎯 Personalized Progress<br/>
                  🏆 Achievement System
                </Text> */}
              </div>
            </div>
          )}
        </Col>
        
        <Col xs={24} lg={12} style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Card 
            style={{ 
              width: '100%', 
              maxWidth: isMobile ? '90vw' : '400px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              borderRadius: '12px',
              padding: isMobile ? '16px 12px' : '24px'
            }}
          >
            <div style={{ 
              textAlign: 'center', 
              marginBottom: isMobile ? '24px' : '32px',
              width: '100%',
              overflow: 'visible'
            }}>
              <img 
                src="/images/logo.png"
                alt="ICER Logo" 
                className="logo-image"
                style={{ 
                  width: isMobile ? 'calc(100% - 20px)' : '260px', 
                  height: isMobile ? 'auto' : '70px',
                  minHeight: isMobile ? '100px' : '100px',
                  maxHeight: isMobile ? '180px' : '100px',
                  // marginBottom: '20px',
                  objectFit: 'contain',
                  // borderRadius: '8px',
                  maxWidth: '100%',
                  // display: 'block',
                  margin: '0 auto 20px auto',
                  transform: isMobile ? 'scale(5)' : 'none'
                }}
                onLoad={(e) => {
                  // Show image and hide fallback when image loads successfully
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'block';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'none';
                  }
                }}
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  console.log('Logo image failed to load');
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'block';
                  }
                }}
              />
              <BookOutlined 
                className="logo-fallback" 
                style={{ 
                  fontSize: isMobile ? '80px' : '80px', 
                  color: '#1890ff', 
                  marginBottom: '20px',
                  display: 'none',
                  width: '100%',
                  textAlign: 'center'
                }} 
              />
              {/* <Title level={2} style={{ marginBottom: '8px' }}>
                Тавтай морил
              </Title>
              <Text type="secondary">
                Мянган бээрийн аялал нэг алхамаас эхэлдэг
              </Text> */}
            </div>

            {/* Alert Message */}
            {alertMessage && (
              <div style={{ marginBottom: '16px' }}>
                <Alert
                  message={alertMessage.message}
                  type={alertMessage.type}
                  showIcon
                  closable
                  onClose={() => setAlertMessage(null)}
                  style={{ 
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}
                />
              </div>
            )}

            <Form
              form={form}
              name="login"
              onFinish={handleLogin}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Мэйлээ бичнэ үү!' },
                  { type: 'email', message: 'Зөв мэйл хаяг оруулна уу!' }
                ]}
              >
                <Input 
                  prefix={<UserOutlined style={{ color: '#1890ff' }} />} 
                  placeholder="Enter your email"
                  autoComplete="email"
                  style={{ 
                    width: '100%', 
                    height: '56px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    border: '2px solid #b4b6e4ff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2a4054ff';
                    e.target.style.boxShadow = '0 4px 12px rgba(24,144,255,0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f0f0f0';
                    e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                  }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Нууц үгээ оруулна уу!' },
                  { min: 6, message: 'Нууц үг хамгийн багадаа 6 тэмдэг байх ёстой!' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#1890ff' }} />}
                  placeholder="Enter your password"
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  autoComplete="current-password"
                  style={{ 
                    width: '100%', 
                    height: '56px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    border: '2px solid #b4b6e4ff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1890ff';
                    e.target.style.boxShadow = '0 4px 12px rgba(24,144,255,0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#f0f0f0';
                    e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                  }}
                />
              </Form.Item>

              <Form.Item>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Form.Item name="remember" valuePropName="checked" noStyle>
                      <Checkbox>Намайг сана</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col>
                    <Button 
                      type="link" 
                      onClick={() => setShowForgotPassword(true)}
                      style={{ color: '#1890ff', padding: 0, height: 'auto' }}
                    >
                      Нууц үгээ мартсан?
                    </Button>
                  </Col>
                </Row>
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  style={{ 
                    width: '100%',
                    maxWidth: '100%',
                    height: '56px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '18px' : '16px',
                    fontWeight: '600',
                    letterSpacing: '0.5px',
                    marginTop: isMobile ? '8px' : '0',
                    background: '#182867',
                    borderColor: '#182867', 
                    boxShadow: isMobile ? '0 4px 16px rgba(24,144,255,0.3)' : '0 2px 8px rgba(24,144,255,0.2)'
                  }}
                >
                  Нэвтрэх
                </Button>
              </Form.Item>

              {/* Test Error Message Button - Remove in production */}
              {/* <Form.Item>
                <Button 
                  type="default" 
                  onClick={handleTestWrongCredentials}
                  style={{ 
                    width: '100%',
                    height: '40px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    opacity: 0.7
                  }}
                >
                  Test Error Message
                </Button>
              </Form.Item> */}
            </Form>

            <Divider>
              <Text type="secondary">эсвэл</Text>
            </Divider>

            <FacebookSignInButton
              onSuccess={handleFacebookLoginSuccess}
              onError={handleFacebookLoginError}
            />
{/* 
            <Divider>
              <Text type="secondary">ICER LMS
              </Text>
            </Divider> */}

            {/* <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                size="large"
                style={{ 
                  width: '100%',
                  height: '48px',
                  borderRadius: '8px'
                }}
                onClick={() => handleSocialLogin('Google')}
              >
                <Space>
                  <span style={{ fontSize: '18px' }}>🔍</span>
                  Continue with Google
                </Space>
              </Button>
              
              <Button 
                size="large"
                style={{ 
                  width: '100%',
                  height: '48px',
                  borderRadius: '8px'
                }}
                onClick={() => handleSocialLogin('GitHub')}
              >
                <Space>
                  <span style={{ fontSize: '18px' }}>⚡</span>
                  Continue with GitHub
                </Space>
              </Button>
            </Space> */}

                        {/* <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <Text type="secondary">
                Don't have an account?{' '}
                <Link to="/register" style={{ color: '#1890ff', fontWeight: '600' }}>
                  Create one here
                </Link>
              </Text>
            </div> */}
          </Card>
        </Col>
      </Row>

      {/* Forgot Password Modal */}
      <Modal
        title={
          <Title level={3} style={{ margin: 0, textAlign: 'center' }}>
            Нууц үг сэргээх
          </Title>
        }
        open={showForgotPassword}
        onCancel={() => {
          setShowForgotPassword(false);
          forgotPasswordForm.resetFields();
          setAlertMessage(null);
        }}
        footer={null}
        width={isMobile ? '90%' : 400}
        centered
        style={{
          borderRadius: '12px'
        }}
      >
        <div style={{ padding: isMobile ? '16px 0' : '24px 0' }}>
          <Text style={{ 
            fontSize: isMobile ? '14px' : '16px',
            color: '#666',
            display: 'block',
            textAlign: 'center',
            marginBottom: '24px'
          }}>
            Бүртгэлтэй имэйл хаягаа оруулна уу. Нууц үг шинэчлэх заавар илгээгдэнэ.
          </Text>

          <Form
            form={forgotPasswordForm}
            layout="vertical"
            onFinish={handleForgotPassword}
          >
            <Form.Item
              name="email"
              label="Имэйл хаяг"
              rules={[
                { required: true, message: 'Имэйл хаягаа оруулна уу!' },
                { type: 'email', message: 'Зөв имэйл хаяг оруулна уу!' }
              ]}
            >
              <Input
                size="large"
                placeholder="example@email.com"
                style={{
                  borderRadius: '8px',
                  fontSize: isMobile ? '14px' : '16px'
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
              <Space style={{ width: '100%', justifyContent: 'center' }} size="middle">
                <Button
                  onClick={() => {
                    setShowForgotPassword(false);
                    forgotPasswordForm.resetFields();
                    setAlertMessage(null);
                  }}
                  style={{
                    borderRadius: '8px',
                    height: '44px',
                    padding: '0 24px',
                    fontSize: '14px'
                  }}
                >
                  Цуцлах
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={forgotPasswordLoading}
                  style={{
                    borderRadius: '8px',
                    height: '44px',
                    padding: '0 24px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Имэйл илгээх
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default Login;
